/**
 * Sinnbilder der Elektrobauteile.
 *
 * --- Warum eigene und nicht die allgemeinen ------------------------------------
 *
 * 52 Bauteilarten. Ein Sinnbild je Art waere zu viel - niemand unterscheidet
 * zwanzig Leuchtensymbole. Zwoelf Gruppen reichen: Wer eine Deckenleuchte von
 * einem Rauchmelder unterscheiden will, braucht kein drittes fuer die
 * Pendelleuchte.
 *
 * --- Zur Farbe -----------------------------------------------------------------
 *
 * Hellblaue Toene, je Anlagengruppe abgestuft. Sie tragen keine Bedeutung im Sinne
 * von gut oder schlecht - dafuer sind die Marken da. Sie helfen beim Ueberfliegen
 * einer Liste mit dreihundert Zeilen: Das Auge findet die Leuchten, ohne zu lesen.
 *
 * Bewusst kein Rot und kein Gruen: Die sind fuer Zustaende vergeben - hellgruen
 * fuer "aus dem Modell", hellgelb fuer "nur in der Datenbank", Warnung fuer
 * "pruefen". Eine dritte Farbebene waere eine zu viel.
 *
 * Alle auf 24 mal 24, Strichstaerke 1.6 - dieselbe Konvention wie Icons.tsx.
 */

type Props = {
  size?: number;
  className?: string;
  title?: string;
};

/**
 * Die Farben je Anlagengruppe.
 *
 * Von dunkel nach hell, damit die Reihenfolge im Baum eine Abstufung ergibt:
 * Starkstrom dunkler als Beleuchtung, Schwachstrom heller.
 */
const Farben = {
  einspeisung: '#1e5a8a',   // Hoch- und Mittelspannung
  verteilung:  '#2c7cb0',   // Niederspannungsinstallation
  licht:       '#4a9fd4',   // Allgemeinbeleuchtung
  sicherheit:  '#6bb6e0',   // Sicherheitsbeleuchtung
  brand:       '#7cc0e8',   // Brandmeldetechnik
  ton:         '#8fcbee',   // Elektroakustik
  video:       '#a3d5f2',   // Sicherheitstechnik
  daten:       '#b5def5',   // Datentechnik
  blitz:       '#8aa8bd',   // Blitzschutz und Erdung
  sonstiges:   '#a8b8c4',   // Sonstiges
} as const;

function Svg({
  size = 18, className, title, farbe, children,
}: Props & { farbe: string; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={farbe}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      style={{ flexShrink: 0, verticalAlign: 'text-bottom' }}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

// === Beleuchtung =============================================================

/** Deckenleuchte: Kreis mit Strahlen. */
export const SymLeuchte = (p: Props) => (
  <Svg {...p} farbe={Farben.licht}>
    <circle cx="12" cy="12" r="4.5" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
  </Svg>
);

/** Lichtband: langes Rechteck. */
export const SymLichtband = (p: Props) => (
  <Svg {...p} farbe={Farben.licht}>
    <rect x="2.5" y="9" width="19" height="6" rx="1.5" />
    <path d="M7 12h.01M12 12h.01M17 12h.01" />
  </Svg>
);

/** Strahler: Kegel nach unten. */
export const SymStrahler = (p: Props) => (
  <Svg {...p} farbe={Farben.licht}>
    <path d="M9 3h6l1 5H8l1-5Z" />
    <path d="M8 8l-3 6M16 8l3 6M12 8v7" />
  </Svg>
);

// === Sicherheitsbeleuchtung ===================================================

/** Notlicht: Leuchte mit Batteriezeichen. */
export const SymNotlicht = (p: Props) => (
  <Svg {...p} farbe={Farben.sicherheit}>
    <circle cx="12" cy="10" r="4" />
    <path d="M8.5 17h7M10 20h4" />
    <path d="M12 7.5v5M10 10h4" />
  </Svg>
);

/** Rettungszeichen: Pfeil im Rechteck. */
export const SymFluchtzeichen = (p: Props) => (
  <Svg {...p} farbe={Farben.sicherheit}>
    <rect x="3" y="6" width="18" height="12" rx="1.5" />
    <path d="M8 12h7M12 9l3 3-3 3" />
  </Svg>
);

// === Niederspannungsinstallation ==============================================

/** Steckdose: Kreis mit zwei Kontakten. */
export const SymSteckdose = (p: Props) => (
  <Svg {...p} farbe={Farben.verteilung}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9 10v4M15 10v4" />
  </Svg>
);

/** Kraftsteckdose: Kreis mit drei Kontakten. */
export const SymKraftsteckdose = (p: Props) => (
  <Svg {...p} farbe={Farben.verteilung}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8 10v3.5M12 9v5M16 10v3.5" />
  </Svg>
);

/** Schalter: Kippe. */
export const SymSchalter = (p: Props) => (
  <Svg {...p} farbe={Farben.verteilung}>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M9 15l6-6M15 9v3M15 9h-3" />
  </Svg>
);

/** Melder: Kreis mit Wellen. */
export const SymMelder = (p: Props) => (
  <Svg {...p} farbe={Farben.verteilung}>
    <circle cx="12" cy="12" r="2.5" />
    <path d="M8 8a5.6 5.6 0 0 0 0 8M16 8a5.6 5.6 0 0 1 0 8" />
    <path d="M5.5 5.5a9.2 9.2 0 0 0 0 13M18.5 5.5a9.2 9.2 0 0 1 0 13" />
  </Svg>
);

/** Verbindungsdose: Quadrat mit Kreuz. */
export const SymDose = (p: Props) => (
  <Svg {...p} farbe={Farben.verteilung}>
    <rect x="4.5" y="4.5" width="15" height="15" rx="1.5" />
    <path d="M12 4.5v15M4.5 12h15" />
  </Svg>
);

/** Verteiler: Schrank mit Reihen. */
export const SymVerteiler = (p: Props) => (
  <Svg {...p} farbe={Farben.verteilung}>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M7 7.5h10M7 12h10M7 16.5h10" />
  </Svg>
);

// === Hoch- und Mittelspannung ==================================================

/** Transformator: zwei Wicklungen. */
export const SymTrafo = (p: Props) => (
  <Svg {...p} farbe={Farben.einspeisung}>
    <circle cx="9" cy="12" r="5.5" />
    <circle cx="15" cy="12" r="5.5" />
  </Svg>
);

/** Schaltanlage: Schrank mit Blitz. */
export const SymSchaltanlage = (p: Props) => (
  <Svg {...p} farbe={Farben.einspeisung}>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M13 7l-3 5h2.5l-1 5 3.5-6H12.5L13 7Z" />
  </Svg>
);

// === Brandmeldetechnik =========================================================

/** Rauchmelder: Kreis mit Rauchwellen. */
export const SymRauchmelder = (p: Props) => (
  <Svg {...p} farbe={Farben.brand}>
    <circle cx="12" cy="13.5" r="6" />
    <circle cx="12" cy="13.5" r="2" />
    <path d="M8 5.5c1.5-1.2 3-1.2 4 0s2.5 1.2 4 0" />
  </Svg>
);

/** Handfeuermelder: Kasten mit Hand. */
export const SymHandmelder = (p: Props) => (
  <Svg {...p} farbe={Farben.brand}>
    <rect x="4.5" y="4.5" width="15" height="15" rx="1.5" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="M12 8.5v-1.5" />
  </Svg>
);

/** Alarmierung: Sirene mit Schall. */
export const SymAlarm = (p: Props) => (
  <Svg {...p} farbe={Farben.brand}>
    <path d="M6 9v6h3l4.5 3.5v-13L9 9H6Z" />
    <path d="M17 9.5a4 4 0 0 1 0 5" />
    <path d="M19.5 7a7.5 7.5 0 0 1 0 10" />
  </Svg>
);

// === Elektroakustik ============================================================

/** Lautsprecher: Kreis mit Membran. */
export const SymLautsprecher = (p: Props) => (
  <Svg {...p} farbe={Farben.ton}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);

// === Sicherheitstechnik ========================================================

/** Kamera: Gehaeuse mit Blickfeld. */
export const SymKamera = (p: Props) => (
  <Svg {...p} farbe={Farben.video}>
    <path d="M3 8.5h11v7H3z" />
    <path d="M14 11l5.5-2.5v7L14 13" />
  </Svg>
);

/** Zutrittsleser: Karte mit Welle. */
export const SymZutritt = (p: Props) => (
  <Svg {...p} farbe={Farben.video}>
    <rect x="4" y="6" width="12" height="12" rx="1.5" />
    <path d="M7.5 12h5" />
    <path d="M18.5 9.5a4 4 0 0 1 0 5" />
  </Svg>
);

// === Datentechnik ==============================================================

/** Datenanschluss: Buchse. */
export const SymDatendose = (p: Props) => (
  <Svg {...p} farbe={Farben.daten}>
    <rect x="4.5" y="7" width="15" height="10" rx="1.5" />
    <path d="M9 7v3h6V7M9 13h6" />
  </Svg>
);

/** Accesspoint: Punkt mit Funkwellen. */
export const SymAccesspoint = (p: Props) => (
  <Svg {...p} farbe={Farben.daten}>
    <circle cx="12" cy="16.5" r="2" />
    <path d="M8.5 13a5 5 0 0 1 7 0" />
    <path d="M5.5 10a9.2 9.2 0 0 1 13 0" />
  </Svg>
);

// === Blitzschutz ===============================================================

/** Fangstange: Stab mit Erdung. */
export const SymBlitzschutz = (p: Props) => (
  <Svg {...p} farbe={Farben.blitz}>
    <path d="M12 3v13" />
    <path d="M7 19h10M9 21.5h6" />
  </Svg>
);

// === Sonstiges =================================================================

/** Was in keine Gruppe passt. */
export const SymSonstiges = (p: Props) => (
  <Svg {...p} farbe={Farben.sonstiges}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 8v4M12 16h.01" />
  </Svg>
);

// === Die Zuordnung =============================================================

/**
 * Welches Sinnbild zu welcher Bauteilart.
 *
 * Zwoelf Gruppen fuer 52 Arten. Wer eine Deckenleuchte von einem Rauchmelder
 * unterscheiden will, braucht kein drittes Sinnbild fuer die Pendelleuchte.
 *
 * Nicht aufgefuehrte Arten bekommen SymSonstiges - eine neue Art faellt damit auf,
 * statt still zu verschwinden.
 */
const Zuordnung: Record<string, (p: Props) => JSX.Element> = {
  // Beleuchtung
  deckenleuchte_rund: SymLeuchte,
  pendelleuchte: SymLeuchte,
  einbauleuchte: SymLeuchte,
  anbauleuchte: SymLeuchte,
  wandleuchte: SymLeuchte,
  entkeimungsleuchte: SymLeuchte,
  lichtband_einbau: SymLichtband,
  lichtband_anbau: SymLichtband,
  einbaustrahler: SymStrahler,
  akzentleuchte: SymStrahler,

  // Sicherheitsbeleuchtung
  notlicht: SymNotlicht,
  notlicht_doppel: SymNotlicht,
  sicherheitsleuchte: SymNotlicht,
  fluchtzeichen: SymFluchtzeichen,

  // Installation
  steckdose: SymSteckdose,
  bodendose: SymSteckdose,
  steckdose_kraft: SymKraftsteckdose,
  festanschluss: SymKraftsteckdose,
  schalter: SymSchalter,
  nothalt: SymSchalter,
  raumthermostat: SymSchalter,
  bewegungsmelder: SymMelder,
  praesenzmelder: SymMelder,
  verbindungsdose: SymDose,
  kombirahmen: SymDose,

  // Verteilung
  unterverteilung: SymVerteiler,
  steuerverteilung: SymVerteiler,
  schienenabgang: SymVerteiler,
  schieneneinspeisung: SymVerteiler,
  nshv: SymVerteiler,

  // Hoch- und Mittelspannung
  trafo: SymTrafo,
  ms_schaltanlage: SymSchaltanlage,
  generator: SymSchaltanlage,

  // Brandmeldetechnik
  rauchmelder: SymRauchmelder,
  rwa_melder: SymRauchmelder,
  handfeuermelder: SymHandmelder,
  koppler: SymDose,
  alarmierung: SymAlarm,
  rwa_zentrale: SymVerteiler,
  fensterantrieb: SymSchalter,

  // Elektroakustik
  lautsprecher: SymLautsprecher,
  lautsprecher_wand: SymLautsprecher,

  // Sicherheitstechnik
  kamera: SymKamera,
  monitor: SymKamera,
  zutrittsleser: SymZutritt,
  fluchttuerterminal: SymZutritt,
  notruftaster: SymHandmelder,

  // Datentechnik
  datendose: SymDatendose,
  accesspoint: SymAccesspoint,

  // Blitzschutz
  fangstange: SymBlitzschutz,
  potentialausgleich: SymBlitzschutz,
  erdungsfestpunkt: SymBlitzschutz,

  // Sonstiges
  abschottung: SymSonstiges,
};

/**
 * Das Sinnbild zu einer Bauteilart.
 *
 * @example
 *   const Sym = symbolFuer('rauchmelder');
 *   <Sym size={14} title="Rauchmelder" />
 */
export function symbolFuer(bauteilart: string): (p: Props) => JSX.Element {
  return Zuordnung[bauteilart] ?? SymSonstiges;
}

/** Die Farbe zu einer Bauteilart - fuer Marken und Balken. */
export function farbeFuer(kostengruppe: string | null): string {
  if (!kostengruppe) return Farben.sonstiges;

  if (kostengruppe.startsWith('441') || kostengruppe.startsWith('442')
      || kostengruppe.startsWith('443')) return Farben.einspeisung;

  if (kostengruppe === '4452') return Farben.sicherheit;
  if (kostengruppe.startsWith('445')) return Farben.licht;
  if (kostengruppe.startsWith('444')) return Farben.verteilung;
  if (kostengruppe.startsWith('446')) return Farben.blitz;
  if (kostengruppe === '4561' || kostengruppe === '4568') return Farben.brand;
  if (kostengruppe === '4562') return Farben.ton;
  if (kostengruppe === '4567' || kostengruppe === '4543') return Farben.video;
  if (kostengruppe.startsWith('457')) return Farben.daten;

  return Farben.sonstiges;
}
